import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import JobsiteApi from '../jobsite/jobsiteApi';
import { Jobsite } from './jobsiteType';
import ProjectApi from '../project/projectApi';
import { Project } from '../project/projectType';
import { GoogleMap, LoadScript, Marker, Autocomplete, Libraries } from '@react-google-maps/api';
import { getSessionData } from '../lib/global_function';
import HorizontalMenu from '../components/common/horizontalMenu';
import Breadcrumbs from '../components/common/breadcrumbs';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select } from '../components/ui/select';
import { Label } from '../components/ui/label';
import Spinner from '../components/ui/spinner';
import { MapPin, FilePlus } from 'lucide-react';
import { US_STATES } from '../reference/referenceData';

const CreateJobsitePage: React.FC = () => {
  const breadcrumbs = [
    { label: 'Project', path: '/main/project' },
    { label: 'Jobsite', path: '/main/project/jobsite' },
    { label: 'New', path: '/main/project/jobsite/create' },
  ];

  const libraries = useRef<Libraries>(['places']);
  const [jobsite, setJobsite] = useState<Partial<Jobsite>>({
    name: '',
    jobsite_description: '',
    project_id: '',
    company_name: '',
    address_1: '',
    address_2: '',
    city: '',
    state: '',
    zip: '',
    latitude: 0,
    longitude: 0,
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [projects, setProjects] = useState<Project[]>([]);
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [mapCenter, setMapCenter] = useState({ lat: 28.0517, lng: -82.4588 });
  const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);
  const navigate = useNavigate();

  // Convert US_STATES enum to array of valid state names, cast to string[]
  const validUsStateNames = Object.values(US_STATES) as string[];

  // State abbreviation to full name mapping
  const stateMap: { [key: string]: string } = {
    'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
    'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
    'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa',
    'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
    'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi',
    'MO': 'Missouri', 'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire',
    'NJ': 'New Jersey', 'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina',
    'ND': 'North Dakota', 'OH': 'Ohio', 'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania',
    'RI': 'Rhode Island', 'SC': 'South Carolina', 'SD': 'South Dakota', 'TN': 'Tennessee',
    'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont', 'VA': 'Virginia', 'WA': 'Washington',
    'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming'
  };

  useEffect(() => {
    const sessionData = getSessionData(process.env.REACT_APP_SESSION_STORAGE_KEY as string);
    const companyName = String(sessionData?.company_name);

    if (typeof companyName === 'string' && companyName.trim()) {
      setCompanyName(companyName);
      setJobsite((prevState) => ({
        ...prevState,
        company_name: companyName,
      }));
    }
  }, []);

  useEffect(() => {
    const fetchProject = async () => {
      setIsLoadingProjects(true);
      try {
        const fetchedProjects = await ProjectApi.get();
        setProjects(fetchedProjects);
      } catch (error) {
        if (error instanceof Error) {
          setErrorMessage(error.message);
        } else {
          setErrorMessage('An unknown error occurred.');
        }
      } finally {
        setIsLoadingProjects(false);
      }
    };
    fetchProject();
  }, []);

  const onLoad = useCallback((autocomplete: google.maps.places.Autocomplete) => {
    setAutocomplete(autocomplete);
  }, []);

  const onPlaceChanged = () => {
    if (autocomplete !== null) {
      const place = autocomplete.getPlace();
      if (place.geometry && place.geometry.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        setMapCenter({ lat, lng });

        let address1 = '';
        let city = '';
        let state = '';
        let zip = '';

        // Parse address components
        for (const component of place.address_components as google.maps.GeocoderAddressComponent[]) {
          const componentType = component.types[0];
          switch (componentType) {
            case 'street_number':
              address1 = `${component.long_name} ${address1}`;
              break;
            case 'route':
              address1 += component.short_name;
              break;
            case 'locality':
            case 'sublocality':
            case 'sublocality_level_1':
            case 'administrative_area_level_2':
              if (!city) city = component.long_name;
              break;
            case 'administrative_area_level_1':
              state = component.long_name;
              break;
            case 'postal_code':
              zip = component.long_name;
              break;
          }
        }

        // Fallback: Parse formatted_address if city or state is missing
        if (!city || !state) {
          const parts = place.formatted_address?.split(',').map(part => part.trim()) || [];
          if (parts.length >= 3) {
            if (!city) city = parts[parts.length - 3] || '';
            if (!state) {
              const statePart = parts[parts.length - 2] || '';
              let stateMatch = validUsStateNames.find(name => 
                statePart.toLowerCase().includes(name.toLowerCase())
              );
              if (!stateMatch) {
                const stateTokens = statePart.split(' ');
                const abbreviationMatch = stateTokens.find(token => 
                  Object.keys(stateMap).includes(token.toUpperCase())
                );
                stateMatch = abbreviationMatch ? stateMap[abbreviationMatch.toUpperCase()] : '';
              }
              state = stateMatch || '';
            }
          }
        }

        // Validate state
        if (state && !validUsStateNames.includes(state)) {
          state = '';
        }

        setJobsite({
          ...jobsite,
          address_1: address1.trim(),
          city: city.trim(),
          state: state.trim(),
          zip: zip.trim(),
          latitude: lat,
          longitude: lng,
        });
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setJobsite({
      ...jobsite,
      [name]: name === 'project_id' ? value : value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    setIsSubmitting(true);

    if (!jobsite.city || !jobsite.state) {
      setErrorMessage('City and state are required. Please select a valid address.');
      setIsSubmitting(false);
      return;
    }

    if (!validUsStateNames.includes(jobsite.state)) {
      setErrorMessage(`Invalid state: "${jobsite.state}". Please select a valid US state.`);
      setIsSubmitting(false);
      return;
    }

    try {
      await JobsiteApi.create(jobsite as Jobsite);
      setMessage('Jobsite created successfully');
      
      // Redirect after a short delay
      setTimeout(() => {
        navigate('/main/project/jobsite');
      }, 2000);
    } catch (error) {
      if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('An unknown error occurred.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateProject = () => {
    navigate('/main/project/create');
  };

  if (isLoadingProjects) {
    return (
      <>
        <HorizontalMenu />
        <Spinner />
      </>
    );
  }

  return (
    <>
      <HorizontalMenu />
      <div className="container-safe py-6">
        <Breadcrumbs items={breadcrumbs} />

        <div className="mt-6">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">

              Create Jobsite
            </h1>
          </div>

          {/* Success Message */}
          {message && (
            <Alert variant="success" className="mb-6">
              {message}
            </Alert>
          )}

          {projects.length > 0 ? (
            <div className="space-y-6">
              {/* Error Message */}
              {errorMessage && (
                <Alert variant="error">
                  {errorMessage}
                </Alert>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Jobsite Information Card */}
                <div className="p-6 bg-white border border-gray-200 rounded-lg">
                  <h2 className="text-lg font-semibold text-foreground mb-4">Jobsite Information</h2>
                  
                  <div className="space-y-4">
                    {/* Jobsite Name */}
                    <div>
                      <Label htmlFor="name">
                        Jobsite Name
                        <span className="text-red-600 ml-1">*</span>
                      </Label>
                      <Input
                        type="text"
                        id="name"
                        name="name"
                        value={jobsite.name || ''}
                        onChange={handleInputChange}
                        placeholder="Enter jobsite name"
                        required
                      />
                    </div>

                    {/* Jobsite Description */}
                    <div>
                      <Label htmlFor="jobsite_description">Jobsite Description</Label>
                      <Input
                        type="text"
                        id="jobsite_description"
                        name="jobsite_description"
                        value={jobsite.jobsite_description || ''}
                        onChange={handleInputChange}
                        placeholder="Optional description"
                      />
                    </div>

                    {/* Project Selection */}
                    <div>
                      <Label htmlFor="project_id">
                        Select Project
                        <span className="text-red-600 ml-1">*</span>
                      </Label>
                      <Select
                        id="project_id"
                        name="project_id"
                        onChange={handleInputChange}
                        value={jobsite.project_id || ''}
                        required
                      >
                        <option value="">Select a project</option>
                        {projects.map((project) => (
                          <option key={project.project_id} value={project.project_id}>
                            {project.name}
                          </option>
                        ))}
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Location Card with Google Maps */}
                <div className="p-6 bg-white border border-gray-200 rounded-lg">
                  <h2 className="text-lg font-semibold text-foreground mb-4">Jobsite Location</h2>
                  
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="address-search">
                        Search Address
                        <span className="text-red-600 ml-1">*</span>
                      </Label>
                      <LoadScript 
                        googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''} 
                        libraries={libraries.current}
                      >
                        <Autocomplete 
                          onLoad={onLoad} 
                          onPlaceChanged={onPlaceChanged} 
                          restrictions={{ country: 'us' }}
                        >
                          <Input
                            type="text"
                            id="address-search"
                            placeholder="Type and select your jobsite address"
                            required
                            className="mb-4"
                          />
                        </Autocomplete>

                        {/* Selected Address Display */}
                        {jobsite.address_1 && (
                          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                            <p className="text-sm font-semibold text-gray-700 mb-1">Selected Address:</p>
                            <p className="text-sm text-foreground">
                              {jobsite.address_1}
                              {jobsite.address_2 && `, ${jobsite.address_2}`}
                            </p>
                            <p className="text-sm text-foreground">
                              {jobsite.city}, {jobsite.state} {jobsite.zip}
                            </p>
                          </div>
                        )}

                        {/* Google Map */}
                        <div className="border border-gray-300 rounded-lg overflow-hidden">
                          <GoogleMap
                            center={mapCenter}
                            zoom={11}
                            mapContainerStyle={{ height: '400px', width: '100%' }}
                          >
                            <Marker position={mapCenter} />
                          </GoogleMap>
                        </div>
                      </LoadScript>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex gap-3">
                  <Button
                    type="submit"
                    size="medium"
                    disabled={isSubmitting}
                  >
                    <FilePlus size={18} />
                    {isSubmitting ? 'Creating...' : 'Create Jobsite'}
                  </Button>
                  <Button
                    type="button"
                    size="medium"
                    variant="inverted"
                    onClick={() => navigate('/main/project/jobsite')}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          ) : (
            /* No Projects Available */
            <div className="p-8 bg-white border border-gray-200 rounded-lg text-center">
              <p className="text-gray-600 mb-4">
                No projects available. Please create a project before adding a jobsite.
              </p>
              <Button size="medium" onClick={handleCreateProject}>
                <FilePlus size={18} />
                Create Project
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CreateJobsitePage;